import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useParams, Navigate } from "@/lib/router-compat";
import { Calendar, Clock, ShareNetwork as Share2, BookOpen, ArrowLeft, CheckCircle as CheckCircle2, TrendUp as TrendingUp, FacebookLogo as Facebook, TwitterLogo as Twitter, LinkedinLogo as Linkedin, Envelope as Mail, MagnifyingGlass as Search, CaretRight as ChevronRight, User } from "@phosphor-icons/react";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from "sonner";
import { getBlogPostBySlug, getAllBlogPosts } from "@/data/blogPosts";
import { BlogFilteredSearchLinks } from "@/components/seo/BlogFilteredSearchLinks";
import { extractContextualLinks } from "@/utils/blogContextualLinks";
import { BlogAlertSubscription } from "@/components/notifications/BlogAlertSubscription";
import { useTranslation } from "@/hooks/useTranslation";
import { useCountry } from "@/hooks/useCountry";
import { pageContent } from "@/utils/translations";
import { useState, useEffect } from "react";
import { isReviewedBlogSlug } from "@/lib/blogEditorial";
import editorialRo from "@/data/translations/ro/blog-editorial.json";
import { BlogStockBlock } from "@/components/blog/BlogStockBlock";
import { BLOG_STOCK_RULES } from "@/lib/blogStock";

// Wikimedia image credit: license label + link for the shared caption renderer.
// Falls back to CC BY-SA 4.0 (all converted images are shared under their original license).
const licenseInfo = (license?: string): { label: string; url: string } => {
  switch ((license || "").trim().toLowerCase()) {
    case "cc by-sa 3.0":
      return { label: "CC BY-SA 3.0", url: "https://creativecommons.org/licenses/by-sa/3.0/ro/" };
    case "cc by 4.0":
      return { label: "CC BY 4.0", url: "https://creativecommons.org/licenses/by/4.0/deed.ro" };
    default:
      return { label: "CC BY-SA 4.0", url: "https://creativecommons.org/licenses/by-sa/4.0/deed.ro" };
  }
};

// Popular Posts cu imagini pentru sidebar
const popularPosts = [
  {
    title: "Top 10 cele mai fiabile mașini second hand 2025",
    slug: "cele-mai-fiabile-masini-second-hand-2025",
    category: "Mărci și modele",
    image: "/hero-roautomarket.jpg",
    views: "15.2K"
  },
  {
    title: "Verificare istoric auto GRATIS",
    slug: "verificare-istoric-auto-gratis-2025",
    category: "Verificări auto",
    image: "/demo-images/bmw-x5-demo.jpg",
    views: "12.8K"
  },
  {
    title: "Tehnici de negociere preț care funcționează",
    slug: "negociere-pret-masina-tehnici-2025",
    category: "Sfaturi",
    image: "/demo-images/audi-a4-demo.jpg",
    views: "10.5K"
  }
];

// Latest Posts pentru sidebar
const latestPosts = [
  {
    title: "Ghid complet: cum să cumperi o mașină second hand",
    slug: "ghid-cumparare-masina-second-hand",
    date: "2025-01-15"
  },
  {
    title: "Cele mai bune SUV-uri second hand sub 15.000€",
    slug: "best-suv-second-hand-2025",
    date: "2025-01-14"
  },
  {
    title: "Verificări esențiale înainte de cumpărare",
    slug: "verificari-esentiale-cumparare",
    date: "2025-01-13"
  },
  {
    title: "Cum eviți kilometrii dați înapoi",
    slug: "evita-kilometrii-dati-inapoi",
    date: "2025-01-12"
  }
];

// Related articles (vor fi afișate la final)
const relatedArticles = [
  {
    title: "Top 5 moduri GRATUITE de a verifica istoricul unei mașini",
    slug: "verificare-istoric-auto-gratis-2025",
    category: "Verificări auto",
    image: "/demo-images/bmw-x5-demo.jpg",
    excerpt: "Descoperă metodele gratuite și eficiente pentru verificarea istoricului unui vehicul: RAR, Politia Romana, verificări VIN și alte resurse esențiale."
  },
  {
    title: "Top 10 cele mai fiabile mașini second hand în 2025",
    slug: "cele-mai-fiabile-masini-second-hand-2025",
    category: "Mărci și modele",
    image: "/demo-images/audi-a4-demo.jpg",
    excerpt: "Analiză detaliată a celor mai fiabile mașini second hand pe piața din România: costuri întreținere, disponibilitate piese, probleme comune."
  },
  {
    title: "7 Tehnici dovedite pentru negocierea prețului la mașini",
    slug: "negociere-pret-masina-tehnici-2025",
    category: "Sfaturi cumpărare",
    image: "/hero-roautomarket.jpg",
    excerpt: "Învață să negociezi eficient prețul unei mașini second hand. Strategii testate de experți pentru a obține cel mai bun deal."
  }
];

const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useTranslation();
  const { country } = useCountry();
  const content = pageContent[language];
  const [readingProgress, setReadingProgress] = useState(0);
  
  const blogPrefix = country === 'UK' ? '/uk/blog' : country === 'DE' ? '/de/blog' : '/blog';
  
  // Încarcă articolul bazat pe slug și țară
  const post = slug ? getBlogPostBySlug(slug, country) : undefined;
  const allPosts = getAllBlogPosts(country);
  
  // Reading progress bar
  useEffect(() => {
    const updateReadingProgress = () => {
      const article = document.querySelector('article');
      if (!article) return;
      
      const articleTop = article.offsetTop;
      const articleHeight = article.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      
      const progress = Math.min(
        100,
        Math.max(0, ((scrollY - articleTop + windowHeight * 0.3) / articleHeight) * 100)
      );
      
      setReadingProgress(progress);
    };
    
    window.addEventListener('scroll', updateReadingProgress);
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);
  
  // Dacă articolul nu există, redirect la 404
  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const heroImage = post.image || "https://roautomarket.ro/hero-roautomarket.jpg";
  const nativeArticleHead = country === 'RO' && isReviewedBlogSlug(post.slug);

  const handleShare = (platform: string) => {

    const url = window.location.href;
    const title = post.title;
    
    let shareUrl = '';
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(content.blogpost_copied);
  };

  const getMetaLang = () => {
    if (country === 'UK') return 'en-GB';
    if (country === 'DE') return 'de';
    return 'ro';
  };

  const getCanonicalUrl = () => {
    if (country === 'UK') return `https://roautomarket.ro/uk/blog/${slug}`;
    if (country === 'DE') return `https://roautomarket.ro/de/blog/${slug}`;
    return `https://roautomarket.ro/blog/${slug}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-muted">
        <div 
          className="h-full bg-gradient-to-r from-primary via-primary to-primary/80 transition-all duration-150 ease-out"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {!nativeArticleHead && <Helmet>
        <html lang={getMetaLang()} />
        <title>{post.seoTitle || `${post.title.slice(0, 42).trimEnd()}${post.title.length > 42 ? '…' : ''} | RO Auto Market`}</title>
        <meta name="description" content={post.excerpt.slice(0, 155)} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl()} />
        
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:image" content={heroImage} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={heroImage} />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": heroImage,
            "datePublished": post.date,
            "dateModified": post.dateModified || post.date,
            "wordCount": post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length : undefined,
            "keywords": post.category || undefined,
            "author": {
              "@type": "Person",
              "name": post.author,
              "jobTitle": post.authorRole || undefined,
              "url": "https://roautomarket.ro/despre-noi"
            },
            "publisher": {
              "@type": "Organization",
              "name": "RO Auto Market",
              "logo": {
                "@type": "ImageObject",
                "url": "https://roautomarket.ro/og-roautomarket.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": getCanonicalUrl()
            }
          })}
        </script>
      </Helmet>}
      <SEOHreflang canonicalUrl={getCanonicalUrl()} />

      {/* Breadcrumbs - Minimal Design */}
      <div className="surface-dark">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">{content.blogpost_breadcrumb_home}</Link>
            <ChevronRight size={14} className="text-muted-foreground/50" />
            <Link to={blogPrefix} className="hover:text-primary transition-colors">{content.blogpost_breadcrumb_blog}</Link>
            <ChevronRight size={14} className="text-muted-foreground/50" />
            <span className="text-foreground font-medium">{post.category}</span>
          </nav>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12 xl:gap-16">
          {/* Main Content */}
          <article className="min-w-0 max-w-3xl">
            {/* Article Header - Clean & Modern */}
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0 font-medium">
                  {post.category}
                </Badge>
                <span className="text-muted-foreground text-sm">•</span>
                <span className="text-muted-foreground text-sm">{post.readTime} {content.blogpost_read_time}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-playfair mb-6 leading-[1.15] tracking-tight text-foreground">
                {post.title}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                {post.excerpt}
              </p>

              {post.image && (
                <figure className="mb-8">
                  <img
                    src={post.image}
                    srcSet={post.image.endsWith("-1600.webp") ? `${post.image.replace("-1600.webp", "-800.webp")} 800w, ${post.image} 1600w` : undefined}
                    sizes="(max-width: 896px) 100vw, 896px"
                    alt={post.imageAlt || post.title}
                    width={1600}
                    height={900}
                    loading="eager"
                    fetchPriority="high"
                    className="w-full h-auto object-cover aspect-[16/9] rounded-xl border border-border/50"
                  />
                  {post.imageCredit && (
                    <figcaption className="mt-2 text-sm text-muted-foreground">
                      {post.imageCaption} Foto: {post.imageCredit}
                      {post.imageSource && (
                        <> · <a href={post.imageSource} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">pagina originală</a></>
                      )}
                      {post.imageLicense && <> · Licență <a href={licenseInfo(post.imageLicense).url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{licenseInfo(post.imageLicense).label}</a> · Modificări: decupare 16:9, redimensionare, conversie WebP; versiunea modificată este distribuită sub aceeași licență</>}
                    </figcaption>
                  )}
                </figure>
              )}
              
              {/* Author & Date - Modern Card Style */}
              <div className="flex flex-wrap items-center justify-between gap-4 py-6 border-y border-border/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <User size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{post.author}</p>
                    {post.authorRole && (
                      <p className="text-xs text-muted-foreground">{post.authorRole}</p>
                    )}
                    {post.reviewedBy && (
                      <p className="text-xs text-muted-foreground">Verificat editorial: {post.reviewedBy}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {new Date(post.date).toLocaleDateString('ro-RO', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    {post.dateModified && (nativeArticleHead || post.seoTitle) && (
                      <p className="text-sm text-muted-foreground">
                        Actualizat: {new Date(post.dateModified).toLocaleDateString('ro-RO', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Share Buttons - Compact */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleShare('facebook')}
                    className="h-9 w-9 rounded-full hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Facebook size={18} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleShare('twitter')}
                    className="h-9 w-9 rounded-full hover:bg-sky-50 hover:text-sky-500"
                  >
                    <Twitter size={18} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleShare('linkedin')}
                    className="h-9 w-9 rounded-full hover:bg-blue-50 hover:text-blue-700"
                  >
                    <Linkedin size={18} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={copyLink}
                    className="gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <Share2 size={16} />
                    <span className="hidden sm:inline">{content.blogpost_copy_link}</span>
                  </Button>
                </div>
              </div>
            </header>

            {/* Quick Summary Card - Modern Design */}
            <Card className="mb-12 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent border-primary/10 shadow-xs">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="text-primary" size={20} />
                  </div>
                  <h3 className="font-semibold text-lg">{content.blogpost_what_learn}</h3>
                </div>
                <ul className="grid gap-3">
                  {post.summary.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-muted-foreground">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-sm font-medium mt-0.5">
                        {index + 1}
                      </span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Article Content - Professional Typography */}
            <div className="prose prose-lg dark:prose-invert max-w-none
              prose-headings:font-playfair prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
              [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:py-4 [&_h2]:px-6 [&_h2]:bg-primary [&_h2]:text-primary-foreground [&_h2]:rounded-lg [&_h2]:shadow-md
              [&_h3]:text-lg [&_h3]:sm:text-xl [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:py-2 [&_h3]:px-4 [&_h3]:bg-primary/10 [&_h3]:text-primary [&_h3]:rounded-md [&_h3]:font-semibold [&_h3]:border-l-4 [&_h3]:border-primary
              prose-p:text-muted-foreground prose-p:leading-[1.85] prose-p:mb-6 prose-p:text-base
              prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline prose-a:transition-colors
              prose-strong:text-foreground prose-strong:font-semibold
              prose-li:text-muted-foreground prose-li:my-1.5 prose-li:leading-[1.75]
              prose-ul:my-6 prose-ul:space-y-1 prose-ol:my-6 prose-ol:space-y-1
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-muted/30 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-8 prose-blockquote:italic prose-blockquote:rounded-r-lg
              prose-code:text-primary prose-code:bg-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-sm prose-code:font-mono prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
              prose-hr:border-border/50 prose-hr:my-10
              prose-table:border-collapse prose-table:w-full prose-table:my-8 prose-table:rounded-lg prose-table:overflow-hidden prose-table:border prose-table:border-border/50
              prose-thead:bg-primary/5
              prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:text-foreground prose-th:text-sm prose-th:border-b prose-th:border-border
              prose-td:px-4 prose-td:py-3 prose-td:border-b prose-td:border-border/50 prose-td:text-muted-foreground prose-td:text-sm
              prose-tr:hover:bg-muted/20 prose-tr:transition-colors
              prose-img:rounded-xl prose-img:shadow-md prose-img:my-8">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ node, src, alt, title }) => {
                    // Captioned figure: title = "caption||credit||sourceUrl[||license]"
                    const [caption, credit, source, license] = (title || "").split("||");
                    const lic = licenseInfo(license);
                    const s = typeof src === "string" ? src : "";
                    const srcSet = s.endsWith("-1600.webp")
                      ? `${s.replace("-1600.webp", "-800.webp")} 800w, ${s} 1600w`
                      : undefined;
                    return (
                      <span className="not-prose my-8 block">
                        <img
                          src={s}
                          srcSet={srcSet}
                          sizes="(max-width: 896px) 100vw, 896px"
                          alt={alt || ""}
                          width={1600}
                          height={900}
                          loading="lazy"
                          decoding="async"
                          className="block w-full h-auto aspect-[16/9] object-cover rounded-xl border border-border/50"
                        />
                        {credit && (
                          <span className="mt-2 block text-sm text-muted-foreground">
                            {caption} Foto: {credit}
                            {source && (
                              <> · <a href={source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">pagina originală</a></>
                            )}
                            <> · Licență <a href={lic.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{lic.label}</a> · Modificări: decupare 16:9, redimensionare, conversie WebP; versiunea modificată este distribuită sub aceeași licență</>
                          </span>
                        )}
                      </span>
                    );
                  },
                  a: ({ node, href, children, className, onClick, ...props }) => {
                    const h = (href || "").trim();
                    const mergedClassName = [
                      "text-primary font-medium hover:underline",
                      className,
                    ]
                      .filter(Boolean)
                      .join(" ");

                    const isHttp = /^https?:\/\//i.test(h);
                    const isHash = h.startsWith("#");
                    const isInternalRoute = h.startsWith("/");

                    if (isHttp) {
                      return (
                        <a
                          {...props}
                          href={h}
                          className={mergedClassName}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            onClick?.(e);
                            if (e.defaultPrevented) return;

                            // Allow modifier-click behavior
                            if (
                              e.metaKey ||
                              e.ctrlKey ||
                              e.shiftKey ||
                              e.altKey ||
                              e.button !== 0
                            ) {
                              return;
                            }

                            // In iframe previews, popups can be blocked.
                            // Try opening a new tab; if blocked, navigate in the same tab.
                            e.preventDefault();
                            const w = window.open(h, "_blank", "noopener,noreferrer");
                            if (!w) window.location.assign(h);
                          }}
                        >
                          {children}
                        </a>
                      );
                    }

                    if (isHash) {
                      return (
                        <a
                          {...props}
                          href={h || "#"}
                          className={mergedClassName}
                          onClick={(e) => {
                            onClick?.(e);
                            if (e.defaultPrevented) return;
                            e.preventDefault();

                            const id = h.slice(1);
                            if (id) {
                              window.history.replaceState(null, "", h);
                              const el = document.getElementById(id);
                              el?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }}
                        >
                          {children}
                        </a>
                      );
                    }

                    if (isInternalRoute) {
                      return (
                        <Link to={h} className={mergedClassName}>
                          {children}
                        </Link>
                      );
                    }

                    return (
                      <a {...props} href={h || "#"} className={mergedClassName} onClick={onClick}>
                        {children}
                      </a>
                    );
                  },
                  h2: ({ node, children, ...props }) => {
                    const raw = Array.isArray(children)
                      ? children
                          .map((c) => (typeof c === "string" ? c : ""))
                          .join("")
                      : typeof children === "string"
                        ? children
                        : "";
                    const match = raw.match(/\s*\{#([A-Za-z0-9_-]+)\}\s*$/);
                    const id = match?.[1];
                    const clean = match ? raw.replace(match[0], "").trim() : undefined;
                    return (
                      <h2 {...props} id={id}>
                        {id ? clean : children}
                      </h2>
                    );
                  },
                  h3: ({ node, children, ...props }) => {
                    const raw = Array.isArray(children)
                      ? children
                          .map((c) => (typeof c === "string" ? c : ""))
                          .join("")
                      : typeof children === "string"
                        ? children
                        : "";
                    const match = raw.match(/\s*\{#([A-Za-z0-9_-]+)\}\s*$/);
                    const id = match?.[1];
                    const clean = match ? raw.replace(match[0], "").trim() : undefined;
                    return (
                      <h3 {...props} id={id}>
                        {id ? clean : children}
                      </h3>
                    );
                  },
                  h4: ({ node, children, ...props }) => {
                    const raw = Array.isArray(children)
                      ? children
                          .map((c) => (typeof c === "string" ? c : ""))
                          .join("")
                      : typeof children === "string"
                        ? children
                        : "";
                    const match = raw.match(/\s*\{#([A-Za-z0-9_-]+)\}\s*$/);
                    const id = match?.[1];
                    const clean = match ? raw.replace(match[0], "").trim() : undefined;
                    return (
                      <h4 {...props} id={id}>
                        {id ? clean : children}
                      </h4>
                    );
                  },
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
            
            {/* Alert Subscription Box */}
            {nativeArticleHead && BLOG_STOCK_RULES[post.slug] && <BlogStockBlock slug={post.slug} />}
            <div className="mt-16">
              <BlogAlertSubscription />
            </div>

            {/* Filtered Search Links — contextual based on article content */}
            {!nativeArticleHead && <div className="mt-8">
              <BlogFilteredSearchLinks
                links={(() => {
                  const ctx = extractContextualLinks(
                    post.title,
                    post.content || "",
                    post.category
                  );
                  return ctx.length >= 2 ? ctx : undefined;
                })()}
              />
            </div>}
            
            {/* Related Articles Section */}
            <section className="mt-16 pt-10 border-t border-border/50">
              <h2 className="text-2xl font-bold font-playfair mb-6">{content.blogpost_related}</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {relatedArticles.map((article) => (
                  <Link 
                    key={article.slug}
                    to={`${blogPrefix}/${article.slug}`}
                    className="group"
                  >
                    <Card className="overflow-hidden h-full hover:shadow-md transition-all duration-300 border-border/50">
                      <div className="aspect-[16/10] overflow-hidden bg-muted">
                        <img 
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-4">
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {article.category}
                        </Badge>
                        <h3 className="font-semibold text-base mb-1.5 group-hover:text-primary transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {article.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>

            {/* Article Footer - Clean CTA */}
            <Card className="mt-16 bg-gradient-to-br from-primary/5 via-transparent to-transparent border-border/50">
              <CardContent className="py-8">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="text-primary" size={24} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold font-playfair mb-2">{content.blogpost_cta_title}</h3>
                  <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                    {nativeArticleHead ? editorialRo.seller_note : content.blogpost_cta_desc}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" asChild>
                      <Link to="/cars">
                        {content.blogpost_cta_explore}
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/cum-functioneaza">
                        {content.blogpost_cta_how}
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

          </article>

          {/* Sidebar - Minimalist Design */}
          <aside className="space-y-6 lg:sticky lg:top-8 lg:self-start hidden lg:block">
            {/* Back to Blog */}
            <Button variant="ghost" size="sm" asChild className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground">
              <Link to="/blog">
                <ArrowLeft size={16} />
                {content.blogpost_back}
              </Link>
            </Button>

            {/* Table of Contents Card */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">În acest articol</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <nav className="space-y-2 text-sm">
                  <a href="#" className="block text-muted-foreground hover:text-primary transition-colors py-1">
                    Introducere
                  </a>
                  <a href="#faq" className="block text-muted-foreground hover:text-primary transition-colors py-1">
                    Întrebări frecvente
                  </a>
                </nav>
              </CardContent>
            </Card>

            {/* Newsletter - Compact */}
            <Card className="bg-gradient-to-br from-primary/5 to-transparent border-border/50">
              <CardContent className="pt-6 pb-6">
                <h3 className="font-semibold mb-2 text-sm">{content.blogpost_sidebar_newsletter_title}</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {content.blogpost_sidebar_newsletter_desc}
                </p>
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder={content.blog_newsletter_placeholder}
                    className="bg-background h-9 text-sm"
                  />
                  <Button className="w-full h-9 text-sm">
                    {content.blog_newsletter_button}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Popular Posts - Minimal */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">{content.blogpost_sidebar_popular}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {popularPosts.slice(0, 3).map((article, index) => (
                  <Link 
                    key={article.slug}
                    to={`${blogPrefix}/${article.slug}`}
                    className="block group"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl font-bold text-muted-foreground/30 group-hover:text-primary/50 transition-colors">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                          {article.title}
                        </h4>
                        <span className="text-xs text-muted-foreground">{article.views} vizualizări</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>

            {/* Latest Posts - Minimal */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">{content.blogpost_sidebar_latest}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {allPosts
                  .filter(p => p.slug !== post.slug)
                  .slice(0, 3)
                  .map((article) => (
                  <Link 
                    key={article.slug}
                    to={`${blogPrefix}/${article.slug}`}
                    className="block group py-2 border-b border-border/30 last:border-0 last:pb-0"
                  >
                    <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2 mb-1">
                      {article.title}
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      {new Date(article.date).toLocaleDateString(getMetaLang(), { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </Link>
                ))}
              </CardContent>
            </Card>

          </aside>
        </div>
      </main>
    </div>
  );
};

export default BlogPost;
